import React from "react";

/**
 * CncMachineIcon — state-aware Manufacturing status icon.
 *   "queued"      → dashed grey circle (no machine)
 *   "processing"  → simplified CNC + bold blue hollow circle badge
 *   "third-party" → amber box + outward arrow (no machine — "sent out")
 *   "complete"    → simplified CNC + emerald filled circle + white tick
 *                   (on-mount "scale-pop + draw-check" animation via CSS classes)
 *   default       → simplified grey CNC + orange power dot (legacy/legend)
 */
export default function CncMachineIcon({ size = 14, state, className = "" }) {
  const s = size;
  const baseProps = {
    width: s * 1.15,
    height: s,
    viewBox: "0 0 18 14",
    className,
    "aria-hidden": "true",
  };

  if (state === "queued") {
    return (
      <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
        <circle cx="6" cy="6" r="4.6" fill="none" stroke="#94a3b8" strokeWidth="1.3" strokeDasharray="2 1.6" />
      </svg>
    );
  }

  if (state === "processing") {
    return (
      <svg {...baseProps}>
        {/* Simplified machine: bed + body + screen */}
        <rect x="0.5" y="10.5" width="11" height="2.2" rx="0.5" fill="#94a3b8" />
        <rect x="1.5" y="2.5" width="9" height="8" rx="0.8" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="0.5" />
        <rect x="3" y="4" width="4.5" height="2.8" rx="0.4" fill="#334155" />
        {/* Blue hollow circle badge — matches StatusGlyph "Ordered" / "Under Inspection" */}
        <circle cx="14.5" cy="3.5" r="3" fill="none" stroke="#3b82f6" strokeWidth="1.7" />
      </svg>
    );
  }

  if (state === "third-party") {
    return (
      <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
        {/* Box outline = the part/package */}
        <rect x="1" y="3" width="7" height="7" rx="0.8" fill="none" stroke="#f59e0b" strokeWidth="1.3" />
        {/* Arrow flying out of box = sent elsewhere */}
        <line x1="6" y1="6" x2="10.5" y2="1.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M8.2 1.2 L10.8 1.2 L10.8 3.8" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    );
  }

  if (state === "complete") {
    return (
      <svg {...baseProps} className={`${className} mfg-complete-pop`}>
        {/* Simplified machine: bed + body + screen */}
        <rect x="0.5" y="10.5" width="11" height="2.2" rx="0.5" fill="#94a3b8" />
        <rect x="1.5" y="2.5" width="9" height="8" rx="0.8" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="0.5" />
        <rect x="3" y="4" width="4.5" height="2.8" rx="0.4" fill="#334155" />
        {/* Emerald filled circle + tick badge */}
        <g className="mfg-complete-tick">
          <circle cx="14.5" cy="3.5" r="3" fill="#10b981" />
          <path
            d="M12.8 3.6 L13.8 4.7 L16.3 2.2"
            stroke="white"
            strokeWidth="1.3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mfg-complete-check-draw"
          />
        </g>
      </svg>
    );
  }

  /* Legacy / legend — simplified grey machine with orange power dot */
  return (
    <svg {...baseProps}>
      <rect x="0.5" y="10.5" width="11" height="2.2" rx="0.5" fill="#94a3b8" />
      <rect x="1.5" y="2.5" width="9" height="8" rx="0.8" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="0.5" />
      <rect x="3" y="4" width="4.5" height="2.8" rx="0.4" fill="#334155" />
      <circle cx="8.5" cy="5" r="0.65" fill="#f97316" />
      <circle cx="8.5" cy="5" r="1.3" fill="#f97316" opacity="0.2" />
    </svg>
  );
}
